import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Wallet, CreditCard, Ban, Check, Tag, UserPlus, Edit, Filter, X, Key, Eye, Trash2, MoveRight, CheckSquare, Square, ExternalLink, ShieldCheck, AlertCircle, AlertTriangle, Clock } from 'lucide-react';
import { handleMobileInput, handleGSTInput, validateMobile, validateGST } from '@/utils/inputValidation';
import { PasswordStrengthChecklist, validatePasswordPolicy } from '@/components/PasswordStrengthChecklist';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const KYCBadge = ({ status }) => {
  const map = {
    in_process: 'bg-amber-100 text-amber-700 border-amber-200',
    approved:   'bg-green-100 text-green-700 border-green-200',
    rejected:   'bg-red-100 text-red-600 border-red-200',
  };
  const labels = { in_process: 'In-Process', approved: 'Approved', rejected: 'Rejected' };
  const cls = map[status] || map.in_process;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${cls} text-nowrap`}>
      {labels[status] || 'In-Process'}
    </span>
  );
};

export default function AdminResellers() {
  const { canDelete, hasPermission } = useAuth();
  const [resellers, setResellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [addResellerDialogOpen, setAddResellerDialogOpen] = useState(false);
  const [editResellerDialogOpen, setEditResellerDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [moveToParentId, setMoveToParentId] = useState('');

  // KYC Management state
  const [activeMainTab, setActiveMainTab] = useState('partners'); // 'partners' | 'kyc' | 'suspend_requests'
  const [kycPartners, setKycPartners] = useState([]);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [rejectModal, setRejectModal] = useState(null); // {id, email}
  const [rejectReason, setRejectReason] = useState('');
  const canVerifyKYC = hasPermission('partners:verify_kyc');

  // Suspend Requests state
  const [suspendRequests, setSuspendRequests] = useState([]);
  const [suspendReqLoading, setSuspendReqLoading] = useState(false);

  // KYC Review Dialog state
  const [kycReviewModal, setKycReviewModal] = useState(null); // {partner, documents, audit_log}
  const [kycReviewLoading, setKycReviewLoading] = useState(false);
  const [docReviewLoading, setDocReviewLoading] = useState(null); // docId being actioned
  const [docRejectDialog, setDocRejectDialog] = useState(null); // {docId, docType, partnerId}
  const [docRejectReason, setDocRejectReason] = useState('');
  const [kycReviewTab, setKycReviewTab] = useState('profile'); // 'profile' | 'documents' | 'audit'
  const [previewBlobUrls, setPreviewBlobUrls] = useState({}); // docId → { url, type }
  const [loadingPreview, setLoadingPreview] = useState(null); // docId being previewed

  // Permission checks
  const canViewPartners = hasPermission('partners:view');
  const canCreatePartner = hasPermission('partners:create');
  const canEditPartner = hasPermission('partners:edit');
  const canSuspendPartner = hasPermission('partners:suspend');
  const canDeletePartner = hasPermission('partners:delete') && canDelete;
  const canCreditPartnerWallet = hasPermission('partners:credit_wallet');
  const canAccessPartnerDashboard = hasPermission('partners:dashboard_access');
  
  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, suspended, cancelled
  const [roleFilter, setRoleFilter] = useState('all'); // all, reseller, sub_reseller
  
  const [discountData, setDiscountData] = useState({
    product_id: '',
    discount_percentage: ''
  });
  const [newResellerData, setNewResellerData] = useState({
    email: '',
    password: '',
    role: 'reseller',
    company_name: '',
    gst_number: '',
    phone: '',
    subdomain: '',
    parent_id: '',
    joining_fee: 0,
    // === TEST MODE BYPASS - set to true only for testing ===
    skip_kyc_test_mode: false
    // === END TEST MODE BYPASS ===
  });
  const [editResellerData, setEditResellerData] = useState({
    email: '',
    company_name: '',
    gst_number: '',
    phone: '',
    subdomain: '',
    parent_id: '',
    state: '',
  });

  useEffect(() => {
    fetchResellers();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchResellers = async () => {
    try {
      const response = await axios.get(`${API}/admin/resellers`);
      setResellers(response.data);
    } catch (error) {
      toast.error('Failed to fetch resellers');
    } finally {
      setLoading(false);
    }
  };

  const fetchKYCPartners = async () => {
    setKycLoading(true);
    try {
      const params = kycStatusFilter !== 'all' ? { status_filter: kycStatusFilter } : {};
      const res = await axios.get(`${API}/admin/partners/kyc`, { params });
      setKycPartners(res.data);
    } catch (e) {
      toast.error('Failed to load KYC partners');
    } finally {
      setKycLoading(false); }
  };

  useEffect(() => {
    if (activeMainTab === 'kyc') fetchKYCPartners();
    if (activeMainTab === 'suspend_requests') fetchSuspendRequests();
  }, [activeMainTab, kycStatusFilter]);

  // Fetch suspend requests count on mount for badge display
  useEffect(() => {
    fetchSuspendRequests();
  }, []);

  const fetchSuspendRequests = async () => {
    setSuspendReqLoading(true);
    try {
      const res = await axios.get(`${API}/admin/suspend-requests`);
      setSuspendRequests(res.data);
    } catch (e) {
      toast.error('Failed to load suspend requests');
    } finally {
      setSuspendReqLoading(false);
    }
  };

  const handleApproveSuspendRequest = async (reqId) => {
    try {
      await axios.put(`${API}/admin/suspend-requests/${reqId}/approve`);
      toast.success('Account suspended');
      fetchSuspendRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectSuspendRequest = async (reqId) => {
    try {
      await axios.put(`${API}/admin/suspend-requests/${reqId}/reject`);
      toast.success('Request rejected — account stays active');
      fetchSuspendRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleApproveKYC = async (partnerId) => {
    try {
      await axios.post(`${API}/admin/partners/${partnerId}/kyc/approve`);
      toast.success('KYC approved');
      fetchKYCPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectKYC = async () => {
    if (!rejectModal) return;
    try {
      await axios.post(`${API}/admin/partners/${rejectModal.id}/kyc/reject`, { reason: rejectReason });
      toast.success('KYC rejected');
      setRejectModal(null);
      setRejectReason('');
      fetchKYCPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleViewKYCDetails = async (partnerId) => {
    setKycReviewLoading(true);
    setKycReviewTab('profile');
    try {
      const res = await axios.get(`${API}/admin/partners/${partnerId}/kyc/details`);
      setKycReviewModal(res.data);
    } catch (e) {
      toast.error('Failed to load KYC details');
    } finally {
      setKycReviewLoading(false);
    }
  };

  const refreshKYCModal = async (partnerId) => {
    try {
      const res = await axios.get(`${API}/admin/partners/${partnerId}/kyc/details`);
      setKycReviewModal(res.data);
    } catch {}
  };

  const handleDocReview = async (partnerId, docId, action, reason = '') => {
    setDocReviewLoading(docId);
    try {
      await axios.put(`${API}/admin/partners/${partnerId}/kyc/documents/${docId}/review`, { action, reason });
      toast.success(`Document ${action === 'approve' ? 'approved' : 'rejected'}`);
      await refreshKYCModal(partnerId);
      setDocRejectDialog(null);
      setDocRejectReason('');
      fetchKYCPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update document');
    } finally {
      setDocReviewLoading(null);
    }
  };

  const handleDocDownload = async (partnerId, doc) => {
    try {
      const res = await axios.get(`${API}/admin/partners/${partnerId}/kyc/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDocPreview = async (partnerId, doc) => {
    if (previewBlobUrls[doc.id]) return; // already loaded
    setLoadingPreview(doc.id);
    try {
      const res = await axios.get(`${API}/admin/partners/${partnerId}/kyc/documents/${doc.id}/download?preview=true`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] || doc.content_type }));
      setPreviewBlobUrls(prev => ({ ...prev, [doc.id]: { url, type: res.headers['content-type'] || doc.content_type } }));
    } catch { toast.error('Preview failed'); }
    finally { setLoadingPreview(null); }
  };

  const handleCredit = async () => {
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await axios.post(`${API}/admin/wallet/credit`, null, {
        params: {
          user_id: selectedReseller.id,
          amount: parseFloat(creditAmount),
          description: creditDescription || 'Wallet credit by admin'
        }
      });
      toast.success('Wallet credited successfully');
      setCreditDialogOpen(false);
      setCreditAmount('');
      setCreditDescription('');
      setSelectedReseller(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit wallet');
    }
  };

  const handleSuspend = async (resellerId) => {
    if (!window.confirm('Are you sure you want to suspend this partner?')) return;
    
    try {
      await axios.put(`${API}/admin/resellers/${resellerId}/suspend`);
      toast.success('Partner suspended successfully');
      fetchResellers();
    } catch (error) {
      toast.error('Failed to suspend partner');
    }
  };

  const handleActivate = async (resellerId) => {
    try {
      await axios.put(`${API}/admin/resellers/${resellerId}/activate`);
      toast.success('Partner activated successfully');
      fetchResellers();
    } catch (error) {
      toast.error('Failed to activate partner');
    }
  };

  const handleCancel = async (resellerId) => {
    if (!window.confirm('Are you sure you want to cancel this partner? This action cannot be undone.')) return;
    
    try {
      await axios.put(`${API}/admin/resellers/${resellerId}/cancel`);
      toast.success('Partner cancelled successfully');
      fetchResellers();
    } catch (error) {
      toast.error('Failed to cancel partner');
    }
  };

  const handleSpecialDiscount = async () => {
    if (!discountData.product_id || !discountData.discount_percentage) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/special-discounts`, {
        reseller_id: selectedReseller.id,
        product_id: discountData.product_id,
        discount_percentage: parseFloat(discountData.discount_percentage)
      });
      toast.success('Special discount applied successfully');
      setDiscountDialogOpen(false);
      setDiscountData({ product_id: '', discount_percentage: '' });
      setSelectedReseller(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply discount');
    }
  };

  const handleCreateReseller = async () => {
    if (!newResellerData.email || !newResellerData.password || !newResellerData.company_name) {
      toast.error('Please fill required fields');
      return;
    }
    if (!validatePasswordPolicy(newResellerData.password)) {
      toast.error('Password does not meet the required policy (min 8 chars, uppercase, lowercase, number, special character)');
      return;
    }

    try {
      await axios.post(`${API}/admin/create-reseller`, newResellerData);
      toast.success('Partner created successfully');
      setAddResellerDialogOpen(false);
      setNewResellerData({
        email: '',
        password: '',
        role: 'reseller',
        company_name: '',
        gst_number: '',
        phone: '',
        subdomain: '',
        parent_id: '',
        joining_fee: 0
      });
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create partner');
    }
  };

  const handleEditReseller = (reseller) => {
    setSelectedReseller(reseller);
    setEditResellerData({
      email: reseller.email,
      company_name: reseller.company_name,
      gst_number: reseller.gst_number || '',
      phone: reseller.phone || '',
      subdomain: reseller.subdomain || '',
      parent_id: reseller.parent_id || '',
      state: reseller.state || '',
    });
    setEditResellerDialogOpen(true);
  };

  const handleUpdateReseller = async () => {
    if (!editResellerData.email || !editResellerData.company_name) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      await axios.put(`${API}/admin/resellers/${selectedReseller.id}`, editResellerData);
      toast.success('Reseller updated successfully');
      setEditResellerDialogOpen(false);
      setSelectedReseller(null);
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reseller');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (!validatePasswordPolicy(newPassword)) {
      toast.error('Password does not meet requirements');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await axios.put(`${API}/admin/resellers/${selectedReseller.id}/change-password`, {
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedReseller(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // View Profile - Reseller data already includes fresh wallet balance from backend
  const handleViewProfile = (reseller) => {
    setSelectedReseller(reseller);
    setProfileDialogOpen(true);
  };

  // Login As (Impersonate) - Opens dashboard in new tab
  const handleLoginAs = async (userId, role) => {
    try {
      const response = await axios.post(`${API}/admin/login-as/${userId}`);
      const { access_token, user } = response.data;
      
      // Determine the dashboard URL based on role
      let dashboardPath = '/reseller/dashboard';
      if (role === 'client') {
        dashboardPath = '/client/dashboard';
      }
      
      // Open new tab with special URL that will auto-login
      const partnerName = encodeURIComponent(user.company_name || user.email || 'Partner');
      const loginUrl = `${BASE_URL}/auto-login?token=${access_token}&redirect=${dashboardPath}&masquerade=1&cn=${partnerName}`;
      window.open(loginUrl, '_blank');
      
      toast.success(`Opening ${user.company_name}'s dashboard in new tab`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to access dashboard');
    }
  };

  // Delete Partner
  const handleDelete = async (resellerId) => {
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete partners');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this partner? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/resellers/${resellerId}`);
      toast.success('Partner deleted successfully');
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete partner');
    }
  };

  // Move Partner
  const handleMove = (reseller) => {
    setSelectedReseller(reseller);
    setMoveToParentId('');
    setMoveDialogOpen(true);
  };

  const handleMoveSubmit = async () => {
    try {
      await axios.post(`${API}/admin/users/move`, {
        user_id: selectedReseller.id,
        new_parent_id: moveToParentId || null
      });
      toast.success('Partner moved successfully');
      setMoveDialogOpen(false);
      setSelectedReseller(null);
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move partner');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedIds.length === filteredResellers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredResellers.map(r => r.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error('No partners selected');
      return;
    }

    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
    if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.length} partner(s)?`)) return;

    try {
      const response = await axios.post(`${API}/admin/users/bulk-action`, {
        user_ids: selectedIds,
        action: action
      });
      
      if (response.data.success > 0) {
        toast.success(`${actionLabel} completed: ${response.data.success} successful, ${response.data.failed} failed`);
      }
      if (response.data.failed > 0 && response.data.errors.length > 0) {
        toast.error(response.data.errors[0]);
      }
      
      setSelectedIds([]);
      setBulkActionOpen(false);
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} partners`);
    }
  };

  // Filter resellers based on status, role, and search
  const filteredResellers = resellers.filter(reseller => {
    const statusMatch = statusFilter === 'all' || 
      (statusFilter === 'active' && reseller.is_active && reseller.status !== 'cancelled') ||
      (statusFilter === 'suspended' && !reseller.is_active && reseller.status !== 'cancelled') ||
      (statusFilter === 'cancelled' && reseller.status === 'cancelled');
    
    const roleMatch = roleFilter === 'all' || reseller.role === roleFilter;
    
    const searchMatch = !searchTerm || 
      reseller.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reseller.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reseller.account_id?.toLowerCase().includes(searchTerm.toLowerCase())||
      reseller.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return statusMatch && roleMatch && searchMatch;
  });

  // Count for filter badges
  const counts = {
    all: resellers.length,
    active: resellers.filter(r => r.is_active && r.status !== 'cancelled').length,
    suspended: resellers.filter(r => !r.is_active && r.status !== 'cancelled').length,
    cancelled: resellers.filter(r => r.status === 'cancelled').length,
    reseller: resellers.filter(r => r.role === 'reseller').length,
    sub_reseller: resellers.filter(r => r.role === 'sub_reseller').length
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setRoleFilter('all');
  };

  const hasActiveFilters = statusFilter !== 'all' || roleFilter !== 'all';

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-start flex-wrap sm-flex-nowrap">
          <div className="mb-3 mr-4">
            <h1 className="text-3xl font-bold text-slate-800" data-testid="resellers-page-title">Partners</h1>
            <p className="text-slate-500 mt-1">Manage your partner network</p>
          </div>
          <div className="flex gap-2 items-center">
            {canCreatePartner && (
              <Button 
                onClick={() => setAddResellerDialogOpen(true)}
                className="bg-cyan-500 hover:bg-cyan-600 text-white sm-2 sm:m-0"
                data-testid="add-reseller-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Partner
              </Button>
            )}
          </div>
        </div>

        {/* Main Tabs — Partners / KYC Verification */}
      <div className="border-b border-slate-200 mb-2">
        <div className="flex gap-2 md:gap-6 mb-2">
          <button
            onClick={() => setActiveMainTab('partners')}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs sm:text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow h-8 px-3 py-1.5 sm:h-9 sm:px-4 sm:py-2 relative 
              ${activeMainTab === 'partners' 
                ? 'bg-[#2578ef] text-white' 
                : 'bg-[#115ab4]/50 text-white hover:text-white'
              }`}
            data-testid="main-tab-partners"
          >
            Partners
          </button>
          {canVerifyKYC && (
            <button
              onClick={() => setActiveMainTab('kyc')}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs sm:text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow h-8 px-3 py-1.5 sm:h-9 sm:px-4 sm:py-2 relative flex items-center gap-1.5 
                ${activeMainTab === 'kyc' 
                  ? 'bg-[#2578ef] text-white' 
                  : 'bg-[#115ab4]/50 text-white hover:text-white'
                }`}
              data-testid="main-tab-kyc"
            >
              <ShieldCheck className="h-4 w-4" />
              KYC Verification
              {kycPartners.filter(p => p.kyc_status === 'in_process').length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  {kycPartners.filter(p => p.kyc_status === 'in_process').length}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setActiveMainTab('suspend_requests')}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs sm:text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow h-8 px-3 py-1.5 sm:h-9 sm:px-4 sm:py-2 relative flex items-center gap-1.5 
              ${activeMainTab === 'suspend_requests' 
                ? 'bg-[#2578ef] text-white' 
                : 'bg-[#115ab4]/50 text-white hover:text-white'
              }`}
            data-testid="main-tab-suspend-requests"
          >
            <AlertTriangle className="h-4 w-4" />
            Suspend Requests
            {suspendRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full" data-testid="suspend-requests-count">
                {suspendRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
        </div>
      </div>


        {/* ── KYC VERIFICATION TAB ── */}
        {activeMainTab === 'kyc' && (
          <div className="space-y-4 pt-2">
            {/* Filters */}
            <div className="flex items-center gap-3">
              {['all', 'in_process', 'approved', 'rejected'].map(s => (
                <button
                  key={s}
                  onClick={() => setKycStatusFilter(s)}
                  className={`text-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${kycStatusFilter === s ? 'bg-[#2578ef] text-white border-[#2578ef]' : 'bg-white border-slate-200 text-slate-600 hover:border-[#2578ef]'}`}
                  data-testid={`kyc-filter-${s}`}
                >
                  {s === 'all' ? 'All' : s === 'in_process' ? 'In-Process' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
              <Button variant="outline" size="sm" onClick={fetchKYCPartners} className="ml-auto">
                Refresh
              </Button>
            </div>

            {/* KYC Partners Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm w-full overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    {['Partner Name', 'Email', 'Type', 'Account ID', 'CRM ID', 'KYC Status', 'Joined', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-medium text-slate-500 text-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kycLoading ? (
                    <tr><td colSpan={7} className="py-10 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2578ef] mx-auto" />
                    </td></tr>
                  ) : kycPartners.length === 0 ? (
                    <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">No partners found</td></tr>
                  ) : kycPartners.map(p => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{p.account_name || p.company_name || '-'}</td>
                      <td className="px-4 py-3 text-slate-600 text-nowrap">{p.email}</td>
                      <td className="px-4 py-3 text-slate-600 text-nowrap">{p.role === 'reseller' ? 'Synergy Partner' : 'Channel Partner'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs font-mono text-nowrap">{p.account_id || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs font-mono text-nowrap">{p.crm_id || '-'}</td>
                      <td className="px-4 py-3"><KYCBadge status={p.kyc_status} /></td>
                      <td className="px-4 py-3 text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-600 hover:border-blue-600 text-xs h-7"
                            onClick={() => handleViewKYCDetails(p.id)}
                            data-testid={`review-kyc-${p.id}`}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          {p.kyc_status !== 'approved' && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                              onClick={() => handleApproveKYC(p.id)}
                              data-testid={`approve-kyc-${p.id}`}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                          {p.kyc_status !== 'rejected' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-600 hover:border-red-600 text-xs h-7"
                              onClick={() => { setRejectModal({ id: p.id, email: p.email }); setRejectReason(''); }}
                              data-testid={`reject-kyc-${p.id}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── PARTNERS TAB (original content) ── */}
        {activeMainTab === 'partners' && (<>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-4 md:gap-8 pt-2">
            <button
              onClick={() => { setStatusFilter('all'); setRoleFilter('all'); }}
              className={`pb-3 text-sm font-medium transition-colors relative
                ${statusFilter === 'all' && roleFilter === 'all' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
              data-testid="tab-resellers"
            >
              All
              {statusFilter === 'all' && roleFilter === 'all' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
            <button
              onClick={() => setRoleFilter('reseller')}

              className={`pb-3 text-sm font-medium transition-colors relative text-nowrap
                ${roleFilter === 'reseller' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
              data-testid="tab-main-resellers"
            >
              Synergy Partners
              {roleFilter === 'reseller' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
            <button
              onClick={() => setRoleFilter('sub_reseller')}
              className={`pb-3 text-sm font-medium transition-colors relative text-nowrap
                ${roleFilter === 'sub_reseller' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
              data-testid="tab-sub-resellers"
            >
              Channel Partners
              {roleFilter === 'sub_reseller' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`pb-3 text-sm font-medium transition-colors relative
                ${statusFilter === 'active' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
              data-testid="tab-status"
            >
              Status
              {statusFilter === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
          </div>
        </div>

        {/* Filters and Bulk Actions */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[120px]"
                  data-testid="filter-status"
                >
                  <option value="all">Filter</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                
                {/* Bulk Actions */}
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-200">
                    <span className="text-sm text-slate-600">{selectedIds.length} selected</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600"
                      onClick={() => handleBulkAction('activate')}
                      data-testid="bulk-activate"
                    >
                      Activate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-orange-600"
                      onClick={() => handleBulkAction('suspend')}
                      data-testid="bulk-suspend"
                    >
                      Suspend
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-gray-600"
                      onClick={() => handleBulkAction('cancel')}
                      data-testid="bulk-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => handleBulkAction('delete')}
                      data-testid="bulk-delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="relative">
                <Input
                  placeholder="Search by name, email, phone..."
                  className="w-40 sm:w-80 bg-white !text-sm md:text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-resellers"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partners Table */}
        <Card className="bg-white border-0 shadow-sm" data-testid="resellers-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <button 
                          onClick={handleSelectAll}
                          className="text-slate-400 hover:text-slate-600"
                          data-testid="select-all"
                        >
                          {selectedIds.length === filteredResellers.length && filteredResellers.length > 0 ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Partner Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Account ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredResellers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-slate-500">
                          No partners found matching the current filters
                        </td>
                      </tr>
                    ) : (
                      filteredResellers.map((reseller) => (
                        <tr key={reseller.id} data-testid={`reseller-row-${reseller.id}`} className={selectedIds.includes(reseller.id) ? 'bg-blue-50' : ''}>
                          <td className="px-4 py-3">
                            <button 
                              onClick={() => handleSelectOne(reseller.id)}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              {selectedIds.includes(reseller.id) ? (
                                <CheckSquare className="h-5 w-5 text-blue-600" />
                              ) : (
                                <Square className="h-5 w-5" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-800">{reseller.company_name}</td>
                          <td className="px-4 py-3 text-xs font-mono text-slate-500 text-nowrap">{reseller.account_id || '—'}</td>
                          <td className="px-4 py-3 text-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              reseller.role === 'reseller' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                            }`}>
                              {reseller.role === 'reseller' ? 'Synergy' : 'Channel'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-blue-600 text-nowrap">{reseller.email}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-nowrap">{reseller.phone || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-nowrap inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                              reseller.status === 'cancelled'
                                ? 'bg-gray-100 text-gray-700'
                                : reseller.is_active 
                                  ? 'bg-green-50 text-green-700' 
                                  : 'bg-red-50 text-red-700'
                            }`}>
                              {reseller.status === 'cancelled' ? 'Cancelled' : reseller.is_active ? 'Active' : 'Suspended'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewProfile(reseller)}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                data-testid={`view-profile-${reseller.id}`}
                                title="View Profile"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {canAccessPartnerDashboard && (
                                <button
                                  onClick={() => handleLoginAs(reseller.id, reseller.role)}
                                  className="text-sm text-cyan-600 hover:text-cyan-800"
                                  data-testid={`login-as-${reseller.id}`}
                                  title="Access Dashboard (New Tab)"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </button>
                              )}
                              {canEditPartner && (
                                <button
                                  onClick={() => handleEditReseller(reseller)}
                                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                  data-testid={`edit-reseller-${reseller.id}`}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canCreditPartnerWallet && reseller.role !== 'sub_reseller' && (
                                <button
                                  onClick={() => {
                                    setSelectedReseller(reseller);
                                    setCreditDialogOpen(true);
                                  }}
                                  className="text-sm text-green-600 hover:text-green-800"
                                  data-testid={`credit-reseller-${reseller.id}`}
                                  title="Credit Wallet"
                                >
                                  <Wallet className="h-4 w-4" />
                                </button>
                              )}
                              {reseller.role === 'sub_reseller' && canEditPartner && (
                                <button
                                  onClick={() => handleMove(reseller)}
                                  className="text-sm text-[#2578ef] hover:text-purple-800"
                                  data-testid={`move-reseller-${reseller.id}`}
                                  title="Move to another parent"
                                >
                                  <MoveRight className="h-4 w-4" />
                                </button>
                              )}
                              {reseller.status !== 'cancelled' && canSuspendPartner && (
                                <>
                                  {reseller.is_active ? (
                                    <button
                                      onClick={() => handleSuspend(reseller.id)}
                                      className="text-sm text-orange-600 hover:text-orange-800"
                                      title="Suspend"
                                    >
                                      <Ban className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleActivate(reseller.id)}
                                      className="text-sm text-green-600 hover:text-green-800"
                                      title="Activate"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                  )}
                                </>
                              )}
                              {canDeletePartner && (
                                <button
                                  onClick={() => handleDelete(reseller.id)}
                                  className="text-sm text-red-600 hover:text-red-800"
                                  data-testid={`delete-reseller-${reseller.id}`}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent data-testid="credit-wallet-dialog">
            <DialogHeader>
              <DialogTitle>Credit Wallet</DialogTitle>
            </DialogHeader>
            {selectedReseller && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-md">
                  <p className="text-sm text-slate-600">Company</p>
                  <p className="font-medium">{selectedReseller.company_name}</p>
                  <p className="text-sm text-slate-600 mt-2">Email</p>
                  <p className="font-medium">{selectedReseller.email}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount (INR)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="5000"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    data-testid="credit-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    placeholder="Initial wallet credit"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    data-testid="credit-description-input"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCredit} data-testid="credit-submit-button">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Credit Wallet
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
          <DialogContent data-testid="special-discount-dialog">
            <DialogHeader>
              <DialogTitle>Apply Special Discount</DialogTitle>
            </DialogHeader>
            {selectedReseller && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-md">
                  <p className="text-sm text-slate-600">Reseller</p>
                  <p className="font-medium">{selectedReseller.company_name}</p>
                  <p className="text-sm text-slate-500">{selectedReseller.email}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product">Select Product</Label>
                  <select
                    id="product"
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    value={discountData.product_id}
                    onChange={(e) => setDiscountData({ ...discountData, product_id: e.target.value })}
                    data-testid="discount-product-select"
                  >
                    <option value="">Choose a product...</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ₹{product.base_price}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discount">Discount Percentage</Label>
                  <Input
                    id="discount"
                    type="number"
                    step="0.01"
                    placeholder="10"
                    value={discountData.discount_percentage}
                    onChange={(e) => setDiscountData({ ...discountData, discount_percentage: e.target.value })}
                    data-testid="discount-percentage-input"
                  />
                  <p className="text-xs text-slate-500">
                    This reseller will get the product at {discountData.discount_percentage || 0}% less than base price
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSpecialDiscount} data-testid="discount-submit-button">
                    <Tag className="h-4 w-4 mr-2" />
                    Apply Discount
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        </>
        )} {/* END activeMainTab === 'partners' */}

        {/* === SUSPEND REQUESTS TAB === */}
        {activeMainTab === 'suspend_requests' && (
          <div className="space-y-4" data-testid="suspend-requests-tab">
            <div className="flex items-center justify-between my-1">
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Suspend Requests
              </h2>
              <Button variant="outline" size="sm" onClick={fetchSuspendRequests}>Refresh</Button>
            </div>
            {suspendReqLoading ? (
              <div className="flex items-center justify-center h-32 text-slate-400">Loading...</div>
            ) : suspendRequests.length === 0 ? (
              <div className="text-center py-12 text-slate-400 bg-white rounded-lg border border-slate-100">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No suspend requests</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                {suspendRequests.map(req => (
                  <div key={req.id} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={`suspend-req-${req.id}`}>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 text-sm">{req.target_name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 capitalize">{req.target_role}</span>
                        {req.status === 'pending' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-200">Pending</span>
                        )}
                        {req.status === 'approved' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium border border-red-200">Approved — Suspended</span>
                        )}
                        {req.status === 'rejected' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium border border-green-200">Rejected — Active</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Requested by <strong>{req.requester_name}</strong> ({req.requester_role}) · {new Date(req.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded p-2 mt-1">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide mr-1">Reason:</span>
                        {req.reason}
                      </p>
                    </div>
                    {req.status === 'pending' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => handleApproveSuspendRequest(req.id)}
                          data-testid={`approve-suspend-${req.id}`}
                        >
                          Suspend
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-700 border-green-200 hover:bg-green-50"
                          onClick={() => handleRejectSuspendRequest(req.id)}
                          data-testid={`reject-suspend-${req.id}`}
                        >
                          Keep Active
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === DIALOGS (rendered regardless of active tab) === */}
        <Dialog open={addResellerDialogOpen} onOpenChange={setAddResellerDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="add-reseller-dialog">
            <DialogHeader>
              <DialogTitle>Add New Partner</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newResellerData.email}
                    onChange={(e) => setNewResellerData({ ...newResellerData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newResellerData.password}
                    onChange={(e) => setNewResellerData({ ...newResellerData, password: e.target.value })}
                    required
                  />
                  <PasswordStrengthChecklist password={newResellerData.password} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={newResellerData.company_name}
                  onChange={(e) => setNewResellerData({ ...newResellerData, company_name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Partner Type</Label>
                  <select
                    id="role"
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    value={newResellerData.role}
                    onChange={(e) => setNewResellerData({ ...newResellerData, role: e.target.value })}
                  >
                    <option value="reseller">Synergy Partner</option>
                    <option value="sub_reseller">Channel Partner</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subdomain">Subdomain</Label>
                  <Input
                    id="subdomain"
                    placeholder="yourcompany"
                    value={newResellerData.subdomain}
                    onChange={(e) => setNewResellerData({ ...newResellerData, subdomain: e.target.value })}
                  />
                </div>
              </div>
              {newResellerData.role === 'sub_reseller' && (
                <div className="space-y-2 mt-4">
                  <Label htmlFor="parent_reseller">Parent Synergy Partner *</Label>
                  <select
                    id="parent_reseller"
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    value={newResellerData.parent_id}
                    onChange={(e) => setNewResellerData({ ...newResellerData, parent_id: e.target.value })}
                    required
                  >
                    <option value="">Select parent partner...</option>
                    {resellers.filter(r => r.role === 'reseller').map((reseller) => (
                      <option key={reseller.id} value={reseller.id}>
                        {reseller.company_name} ({reseller.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">Channel Partner will be linked to this Synergy Partner</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gst">GST Number</Label>
                  <Input
                    id="gst"
                    value={newResellerData.gst_number}
                    onChange={(e) => setNewResellerData({ ...newResellerData, gst_number: handleGSTInput(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newResellerData.phone}
                    onChange={(e) => setNewResellerData({ ...newResellerData, phone: handleMobileInput(e.target.value) })}
                  />
                </div>
              </div>
              {/* === TEST MODE BYPASS — Remove before production === */}
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Skip KYC Verification (Test Mode)</p>
                    <p className="text-xs text-amber-600 mt-0.5">Partner's KYC will be approved immediately. Disable before going live.</p>
                  </div>
                  <Switch
                    checked={newResellerData.skip_kyc_test_mode}
                    onCheckedChange={(val) => setNewResellerData({ ...newResellerData, skip_kyc_test_mode: val })}
                    data-testid="skip-kyc-toggle"
                  />
                </div>
              </div>
              {/* === END TEST MODE BYPASS === */}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setAddResellerDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateReseller} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Partner
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={editResellerDialogOpen} onOpenChange={setEditResellerDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-reseller-dialog">
            <DialogHeader>
              <DialogTitle>Edit Partner</DialogTitle>
            </DialogHeader>
            {selectedReseller && (
              <div className="space-y-4">
                <div className="p-4 rounded-md bg-[#2563eb]/10">
                  <p className="text-sm text-slate-600">Partner Type</p>
                  <p className="font-medium">{selectedReseller.role === 'sub_reseller' ? 'Channel Partner' : 'Synergy Partner'}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editResellerData.email}
                    onChange={(e) => setEditResellerData({ ...editResellerData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-company">Company Name</Label>
                  <Input
                    id="edit-company"
                    value={editResellerData.company_name}
                    onChange={(e) => setEditResellerData({ ...editResellerData, company_name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-subdomain">Subdomain</Label>
                    <Input
                      id="edit-subdomain"
                      placeholder="yourcompany"
                      value={editResellerData.subdomain}
                      onChange={(e) => setEditResellerData({ ...editResellerData, subdomain: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-gst">GST Number</Label>
                    <Input
                      id="edit-gst"
                      value={editResellerData.gst_number}
                      onChange={(e) => setEditResellerData({ ...editResellerData, gst_number: handleGSTInput(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editResellerData.phone}
                    onChange={(e) => setEditResellerData({ ...editResellerData, phone: handleMobileInput(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-state">State</Label>
                  <Input
                    id="edit-state"
                    placeholder="e.g. Rajasthan"
                    value={editResellerData.state}
                    onChange={(e) => setEditResellerData({ ...editResellerData, state: e.target.value })}
                    data-testid="edit-reseller-state"
                  />
                </div>
                {selectedReseller.role === 'sub_reseller' && (
                  <div className="space-y-2 mt-2">
                    <Label htmlFor="edit-parent">Parent Synergy Partner</Label>
                    <select
                      id="edit-parent"
                      className="w-full border border-slate-300 rounded-md px-3 py-2"
                      value={editResellerData.parent_id}
                      onChange={(e) => setEditResellerData({ ...editResellerData, parent_id: e.target.value })}
                    >
                      <option value="">No parent</option>
                      {resellers.filter(r => r.role === 'reseller' && r.id !== selectedReseller.id).map((reseller) => (
                        <option key={reseller.id} value={reseller.id}>
                          {reseller.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setEditResellerDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateReseller} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Edit className="h-4 w-4 mr-2" />
                    Update Partner
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent data-testid="change-password-dialog">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            {selectedReseller && (
              <div className="space-y-4">
                <div className="p-4 rounded-md bg-[#2563eb]/10">
                  <p className="font-medium">{selectedReseller.company_name}</p>
                  <p className="text-sm text-slate-500">{selectedReseller.email}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Min 8 chars, upper, lower, number, special"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                  <PasswordStrengthChecklist password={newPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password-input"
                  />
                </div>
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-sm text-red-600">Passwords do not match</p>
                )}
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleChangePassword} 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
                    data-testid="change-password-submit"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
            <DialogHeader>
              <DialogTitle>Partner Profile</DialogTitle>
            </DialogHeader>
            {selectedReseller && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Company Name</p>
                    <p className="font-semibold text-slate-800">{selectedReseller.company_name}</p>
                  </div>
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Partner Type</p>
                    <p className="font-semibold text-slate-800">
                      {selectedReseller.role === 'reseller' ? 'Synergy Partner' : 'Channel Partner'}
                    </p>
                  </div>
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Email</p>
                    <p className="font-semibold text-blue-600">{selectedReseller.email}</p>
                  </div>
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Phone</p>
                    <p className="font-semibold text-slate-800">{selectedReseller.phone || 'Not provided'}</p>
                  </div>
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">GST Number</p>
                    <p className="font-semibold text-slate-800">{selectedReseller.gst_number || 'Not provided'}</p>
                  </div>
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Subdomain</p>
                    <p className="font-semibold text-slate-800">{selectedReseller.subdomain || 'Not set'}</p>
                  </div>
                  {selectedReseller.role !== 'sub_reseller' && (
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Wallet Balance</p>
                    <p className="font-semibold text-green-600">₹{(selectedReseller.wallet_balance || 0).toLocaleString()}</p>
                  </div>
                  )}
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                      selectedReseller.status === 'cancelled'
                        ? 'bg-gray-100 text-gray-700'
                        : selectedReseller.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                    }`}>
                      {selectedReseller.status === 'cancelled' ? 'Cancelled' : selectedReseller.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </div>
                  <div className="bg-[#06b6d408] p-4 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Joined</p>
                    <p className="font-semibold text-slate-800">
                      {selectedReseller.created_at ? new Date(selectedReseller.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between pt-4 border-t">
                  <div className="flex gap-2">
                    {canAccessPartnerDashboard && (
                      <Button
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                        onClick={() => {
                          setProfileDialogOpen(false);
                          handleLoginAs(selectedReseller.id, selectedReseller.role);
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Access Dashboard
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setProfileDialogOpen(false);
                        handleEditReseller(selectedReseller);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setProfileDialogOpen(false);
                        setSelectedReseller(selectedReseller);
                        setNewPassword('');
                        setConfirmPassword('');
                        setPasswordDialogOpen(true);
                      }}
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Change Password
                    </Button>
                  </div>
                  <Button variant="outline" onClick={() => setProfileDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Move Partner Dialog */}
        <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
          <DialogContent className="max-w-md" data-testid="move-dialog">
            <DialogHeader>
              <DialogTitle>Move Channel Partner</DialogTitle>
            </DialogHeader>
            {selectedReseller && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Moving: <span className="font-semibold">{selectedReseller.company_name}</span>
                </p>
                <div className="space-y-2">
                  <Label>Select New Parent (Synergy Partner)</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    value={moveToParentId}
                    onChange={(e) => setMoveToParentId(e.target.value)}
                  >
                    <option value="">Direct to Admin</option>
                    {resellers
                      .filter(r => r.role === 'reseller' && r.id !== selectedReseller.id)
                      .map(r => (
                        <option key={r.id} value={r.id}>
                          {r.company_name} ({r.email})
                        </option>
                      ))
                    }
                  </select>
                  <p className="text-xs text-slate-500">
                    Select "Direct to Admin" to move under admin management
                  </p>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleMoveSubmit} className="bg-[#2578ef] hover:bg-purple-700 text-white">
                    <MoveRight className="h-4 w-4 mr-2" />
                    Move Partner
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* KYC Reject Reason Modal */}
        <Dialog open={!!rejectModal} onOpenChange={(o) => { if (!o) setRejectModal(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Reject KYC</DialogTitle>
            </DialogHeader>
            {rejectModal && (
              <div className="space-y-4 pt-1">
                <p className="text-sm text-slate-600">Rejecting KYC for: <strong>{rejectModal.email}</strong></p>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1.5">Rejection Reason</label>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Explain the reason for rejection..."
                    rows={3}
                    className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                    data-testid="reject-reason-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setRejectModal(null)}>Cancel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectKYC} data-testid="confirm-reject-kyc">
                    Reject KYC
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* KYC Review Dialog — Full Partner Details */}
        <Dialog open={!!kycReviewModal} onOpenChange={(o) => { if (!o) { setKycReviewModal(null); setDocRejectDialog(null); setPreviewBlobUrls({}); } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between pr-8">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                  KYC Review — {kycReviewModal?.partner?.account_name || kycReviewModal?.partner?.company_name || kycReviewModal?.partner?.email}
                </span>
                {kycReviewModal?.partner && (
                  <div className="flex items-center gap-2">
                    <KYCBadge status={kycReviewModal.partner.kyc_status} />
                  </div>
                )}
              </DialogTitle>
            </DialogHeader>

            {kycReviewLoading ? (
              <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
            ) : kycReviewModal && (
              <div className="space-y-4 pt-2">
                {/* Review Tabs */}
                <div className="flex gap-6 border-b border-slate-200">
                  {[
                    { id: 'profile', label: 'Profile & Account' },
                    { id: 'documents', label: `Documents (${kycReviewModal.documents?.length || 0})` },
                    { id: 'audit', label: `Audit Trail (${kycReviewModal.audit_log?.length || 0})` }
                  ].map(t => (
                    <button
                      key={t.id}
                      onClick={() => setKycReviewTab(t.id)}
                      className={`pb-2 text-sm font-medium border-b-2 transition-colors ${kycReviewTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Profile & Account Tab */}
                {kycReviewTab === 'profile' && (
                  <div className="space-y-4 mt-2">
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">User Profile</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: 'Full Name', value: kycReviewModal.partner.account_name || kycReviewModal.partner.company_name },
                          { label: 'Email', value: kycReviewModal.partner.email },
                          { label: 'Mobile', value: `${kycReviewModal.partner.mobile_country_code || ''} ${kycReviewModal.partner.mobile || kycReviewModal.partner.phone || '—'}`.trim() },
                          { label: 'Role', value: kycReviewModal.partner.role === 'reseller' ? 'Synergy Partner' : 'Channel Partner' },
                          { label: 'CRM ID', value: kycReviewModal.partner.crm_id || '—', mono: true },
                          { label: 'Account ID', value: kycReviewModal.partner.account_id || '—', mono: true },
                          { label: 'KYC Status', badge: kycReviewModal.partner.kyc_status },
                        ].map(f => (
                          <div key={f.label} className="bg-slate-50 rounded-lg p-3">
                            <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{f.label}</p>
                            {f.badge ? (
                              <div className="mt-1"><KYCBadge status={f.badge} /></div>
                            ) : (
                              <p className={`text-sm text-slate-800 mt-1 ${f.mono ? 'font-mono text-xs' : ''}`}>{f.value || '—'}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Account & Business Details</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: 'Company Name', value: kycReviewModal.partner.company_name },
                          { label: 'Account Type', value: kycReviewModal.partner.account_type },
                          { label: 'Account Category', value: kycReviewModal.partner.account_category },
                          { label: 'Account Status', value: kycReviewModal.partner.account_status },
                          { label: 'Industry Group', value: kycReviewModal.partner.industry_group },
                          { label: 'Sub-Vertical', value: kycReviewModal.partner.sub_vertical },
                          { label: 'GST Number', value: kycReviewModal.partner.gst_number, mono: true },
                          { label: 'Website', value: kycReviewModal.partner.website },
                        ].map(f => (
                          <div key={f.label} className="bg-slate-50 rounded-lg p-3">
                            <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{f.label}</p>
                            <p className={`text-sm text-slate-800 mt-1 ${f.mono ? 'font-mono text-xs' : ''}`}>{f.value || '—'}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Tax & Regulatory IDs</h3>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'PAN', value: kycReviewModal.partner.pan },
                          { label: 'TAN', value: kycReviewModal.partner.tan },
                          { label: 'DUNS', value: kycReviewModal.partner.duns },
                        ].map(f => (
                          <div key={f.label} className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                            <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wide">{f.label}</p>
                            <p className="text-sm font-mono text-slate-800 mt-1">{f.value || '—'}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Overall KYC Actions */}
                    <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-500 mr-auto">Overall KYC decision:</span>
                      {kycReviewModal.partner.kyc_status !== 'approved' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={async () => {
                            await handleApproveKYC(kycReviewModal.partner.id);
                            await refreshKYCModal(kycReviewModal.partner.id);
                          }}
                          data-testid="review-approve-kyc"
                        >
                          <Check className="h-4 w-4 mr-1" /> Approve KYC
                        </Button>
                      )}
                      {kycReviewModal.partner.kyc_status !== 'rejected' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => {
                            setRejectModal({ id: kycReviewModal.partner.id, email: kycReviewModal.partner.email });
                            setRejectReason('');
                            setKycReviewModal(null);
                          }}
                          data-testid="review-reject-kyc"
                        >
                          <X className="h-4 w-4 mr-1" /> Reject KYC
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Documents Tab */}
                {kycReviewTab === 'documents' && (
                  <div className="space-y-3 mt-2">
                    {kycReviewModal.documents?.length === 0 ? (
                      <div className="text-center py-10">
                        <AlertCircle className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">No documents uploaded yet</p>
                      </div>
                    ) : kycReviewModal.documents?.map(doc => (
                      <div key={doc.id} className={`rounded-xl border p-4 ${doc.doc_status === 'rejected' ? 'border-red-200 bg-red-50/30' : doc.doc_status === 'approved' ? 'border-green-200 bg-green-50/20' : 'border-slate-200 bg-white'} mt-2`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1 mt-2">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              {doc.category && (
                                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">
                                  {doc.category === 'id_proof' ? 'ID Proof' : 'Address Proof'}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded border text-[10px] font-semibold ${
                                doc.doc_status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                doc.doc_status === 'rejected' ? 'bg-red-100 text-red-600 border-red-200' :
                                'bg-amber-100 text-amber-700 border-amber-200'
                              }`}>
                                {doc.doc_status === 'approved' ? 'Approved' : doc.doc_status === 'rejected' ? 'Rejected' : 'In Review'}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800">{doc.document_type}</p>
                            {doc.document_name && <p className="text-xs text-slate-600 mt-0.5">{doc.document_name}</p>}
                            <p className="text-xs text-slate-400 mt-0.5">
                              {doc.filename} • {new Date(doc.uploaded_at).toLocaleString('en-IN')}
                              {doc.doc_reviewed_by && ` • Reviewed by: ${doc.doc_reviewed_by}`}
                            </p>
                            {doc.doc_status === 'rejected' && doc.doc_rejection_reason && (
                              <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-xs font-medium text-red-700">Rejection reason:</p>
                                <p className="text-xs text-red-600 mt-0.5">{doc.doc_rejection_reason}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Preview trigger */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs h-7"
                              disabled={loadingPreview === doc.id}
                              onClick={() => handleDocPreview(kycReviewModal.partner.id, doc)}
                              data-testid={`admin-preview-doc-${doc.id}`}
                            >
                              {loadingPreview === doc.id ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" /> : <Eye className="h-3 w-3 mr-1" />}
                              {previewBlobUrls[doc.id] ? 'Reload' : 'Preview'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-slate-600 border-slate-200 hover:bg-slate-50 text-xs h-7"
                              onClick={() => handleDocDownload(kycReviewModal.partner.id, doc)}
                              data-testid={`admin-download-doc-${doc.id}`}
                            >
                              <Eye className="h-3 w-3 mr-1" /> Download
                            </Button>
                            {doc.doc_status !== 'approved' && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                                disabled={docReviewLoading === doc.id}
                                onClick={() => handleDocReview(kycReviewModal.partner.id, doc.id, 'approve')}
                                data-testid={`admin-approve-doc-${doc.id}`}
                              >
                                {docReviewLoading === doc.id ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Check className="h-3 w-3 mr-1" />}
                                Approve
                              </Button>
                            )}
                            {doc.doc_status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7"
                                disabled={docReviewLoading === doc.id}
                                onClick={() => setDocRejectDialog({ docId: doc.id, docType: doc.document_type, partnerId: kycReviewModal.partner.id })}
                                data-testid={`admin-reject-doc-${doc.id}`}
                              >
                                <X className="h-3 w-3 mr-1" /> Reject
                              </Button>
                            )}
                          </div>
                        </div>
                        {/* Inline Preview */}
                        {previewBlobUrls[doc.id] && (
                          <div className="mt-3 border-t border-slate-100 pt-3">
                            {previewBlobUrls[doc.id].type?.startsWith('image/') ? (
                              <img
                                src={previewBlobUrls[doc.id].url}
                                alt={doc.document_type}
                                className="max-w-full max-h-80 rounded-lg border border-slate-200 cursor-pointer object-contain"
                                onClick={() => window.open(previewBlobUrls[doc.id].url, '_blank')}
                                data-testid={`preview-img-${doc.id}`}
                              />
                            ) : previewBlobUrls[doc.id].type === 'application/pdf' ? (
                              <iframe
                                src={previewBlobUrls[doc.id].url}
                                title={doc.document_type}
                                className="w-full h-80 rounded-lg border border-slate-200"
                                data-testid={`preview-pdf-${doc.id}`}
                              />
                            ) : (
                              <p className="text-xs text-slate-500 p-3 bg-slate-50 rounded-lg">
                                Preview not available for this file type.{' '}
                                <button onClick={() => handleDocDownload(kycReviewModal.partner.id, doc)} className="text-blue-600 underline">Download to view</button>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Audit Trail Tab */}
                {kycReviewTab === 'audit' && (
                  <div className="space-y-2 mt-2">
                    {kycReviewModal.audit_log?.length === 0 ? (
                      <p className="text-center py-8 text-sm text-slate-400">No audit events yet</p>
                    ) : kycReviewModal.audit_log?.map(log => (
                      <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50 mt-2">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          log.action?.includes('approved') ? 'bg-green-500' :
                          log.action?.includes('rejected') ? 'bg-red-500' : 'bg-blue-400'
                        }`} />
                        <div>
                          <p className="text-xs font-medium text-slate-700 capitalize">{log.action?.replace(/_/g, ' ')}</p>
                          {log.details?.reason && <p className="text-xs text-slate-500 mt-0.5">Reason: {log.details.reason}</p>}
                          {log.details?.document_type && <p className="text-xs text-slate-500 mt-0.5">Document: {log.details.document_type} ({log.details.filename})</p>}
                          <p className="text-[10px] text-slate-400 mt-1">
                            {log.performed_by_email} • {new Date(log.performed_at).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Per-Document Reject Reason Dialog */}
        <Dialog open={!!docRejectDialog} onOpenChange={(o) => { if (!o) { setDocRejectDialog(null); setDocRejectReason(''); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Reject Document</DialogTitle>
            </DialogHeader>
            {docRejectDialog && (
              <div className="space-y-4 pt-1">
                <p className="text-sm text-slate-600">
                  Rejecting: <strong>{docRejectDialog.docType}</strong>
                </p>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1.5">Reason for rejection <span className="text-red-500">*</span></label>
                  <textarea
                    value={docRejectReason}
                    onChange={e => setDocRejectReason(e.target.value)}
                    placeholder="e.g. PAN document is unclear, please re-upload a clear scan..."
                    rows={3}
                    className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                    data-testid="doc-reject-reason-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setDocRejectDialog(null); setDocRejectReason(''); }}>Cancel</Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    disabled={!docRejectReason.trim() || !!docReviewLoading}
                    onClick={() => handleDocReview(docRejectDialog.partnerId, docRejectDialog.docId, 'reject', docRejectReason)}
                    data-testid="confirm-doc-reject"
                  >
                    Reject Document
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
